/**
 * Upload helper: takes a File from an <input type="file"/>, resizes it to a
 * web-friendly size (1600 px max, JPEG 85%) and uploads it to Cloudinary via
 * the backend, returning the secure URL.
 *
 * No fallback to base64. If Cloudinary is not configured or the upload fails
 * the user gets a clear error and retries.
 *
 * We don't pre-check `/cloudinary/status`; we just attempt the upload and
 * surface the backend's error message (400 with detail). This avoids stale
 * cache problems where a user that opened the app before Cloudinary was
 * configured still thinks it isn't.
 */
import { resizeImageFile } from "@/lib/imageResize";
import api from "@/lib/api";

/** Kept for backwards compatibility with callers — now a no-op. */
export function resetCloudinaryStatus() { /* no cache anymore */ }

/**
 * Resize + upload a single image File to Cloudinary.
 * @param {File|Blob} file
 * @param {{folder?: string, maxSide?: number, quality?: number}} opts
 * @returns {Promise<string>} Cloudinary secure_url
 * @throws  {Error}            when the upload fails (with backend message)
 */
export async function uploadPhoto(file, opts = {}) {
  const { folder = "alendrapc/products", maxSide = 1600, quality = 0.85 } = opts;
  const dataUrl = await resizeImageFile(file, { maxSide, quality });
  try {
    const { data } = await api.post("/cloudinary/upload",
      { image: dataUrl, folder }, { timeout: 60000 });
    if (!data?.url) throw new Error("Cloudinary no devolvió URL");
    return data.url;
  } catch (err) {
    // Surface backend error message ("Cloudinary no está configurado…", quota, etc.)
    const msg = err?.response?.data?.detail || err?.message || "Error subiendo la imagen";
    throw new Error(msg);
  }
}
